import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import DiscountConfiguration from '@/features/discounts/components/DiscountConfiguration'
import UnitTypesManager from '@/features/units/components/UnitTypesManager'
import UnitsManager from '@/features/units/components/UnitsManager'
import ProjectImagesManager from '@/features/projects/components/ProjectImagesManager'
import SchemeUploadManager from '@/features/projects/components/SchemeUploadManager'
import ProjectQA from '@/features/communication/components/ProjectQA'
import MessageCenter from '@/features/communication/components/MessageCenter'
import MilestoneManager from '@/features/milestones/components/MilestoneManager'
import DeveloperUpdates from '@/features/milestones/components/DeveloperUpdates'

export default function ManageProjectPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('details')

  const { data: projectData, isLoading: projectLoading } = useQuery({
    queryKey: ['developer-project', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data
    },
  })

  const { data: unitsData, isLoading: unitsLoading } = useQuery({
    queryKey: ['project-units', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('units')
        .select('*')
        .eq('project_id', id)
        .order('unit_number')

      if (error) throw error
      return data
    },
  })

  const { data: unansweredQuestionsData } = useQuery({
    queryKey: ['unanswered-questions', id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('project_questions')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', id)
        .is('answer', null)

      if (error) throw error
      return count || 0
    },
  })

  const { data: unreadMessagesData } = useQuery({
    queryKey: ['unread-messages-project', id],
    queryFn: async () => {
      const { data: conversations, error: convError } = await supabase
        .from('project_conversations')
        .select('id')
        .eq('project_id', id)

      if (convError || !conversations) return 0

      let totalUnread = 0
      for (const conv of conversations) {
        const { data: messages } = await supabase
          .from('conversation_messages')
          .select('*, sender:sender_id(role)')
          .eq('conversation_id', conv.id)
          .is('read_at', null)

        const unreadFromBuyers = messages?.filter(m => m.sender?.role === 'buyer').length || 0
        totalUnread += unreadFromBuyers
      }

      return totalUnread
    },
  })

  if (projectLoading || unitsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading project...</p>
        </div>
      </div>
    )
  }

  const project = projectData
  const units = unitsData || []
  const unansweredCount = unansweredQuestionsData || 0
  const unreadCount = unreadMessagesData || 0

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard/developer')}
          className="text-primary-600 hover:text-primary-700 mb-4 flex items-center gap-2"
        >
          ← Back to Dashboard
        </button>
        <h1 className="text-4xl font-bold text-secondary-900 mb-2">
          {project.name}
        </h1>
        <p className="text-secondary-600">{project.location}</p>
      </div>

      <div className="flex space-x-1 mb-6 border-b border-secondary-200">
        <button
          onClick={() => setActiveTab('details')}
          className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
            activeTab === 'details'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-secondary-600 hover:text-secondary-900'
          }`}
        >
          Project Details
        </button>
        <button
          onClick={() => setActiveTab('units')}
          className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
            activeTab === 'units'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-secondary-600 hover:text-secondary-900'
          }`}
        >
          Units ({units.length})
        </button>
        <button
          onClick={() => setActiveTab('discounts')}
          className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
            activeTab === 'discounts'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-secondary-600 hover:text-secondary-900'
          }`}
        >
          Discounts
        </button>
        <button
          onClick={() => setActiveTab('qa')}
          className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 flex items-center gap-2 ${
            activeTab === 'qa'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-secondary-600 hover:text-secondary-900'
          }`}
        >
          Q&A
          {unansweredCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-amber-500 text-white text-xs font-bold rounded-full">
              {unansweredCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('messages')}
          className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 flex items-center gap-2 ${
            activeTab === 'messages'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-secondary-600 hover:text-secondary-900'
          }`}
        >
          Messages
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-blue-500 text-white text-xs font-bold rounded-full">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'details' && (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="card p-6">
              <ProjectImagesManager projectId={id} developerId={project.developer_id} />
            </div>

            <div className="card p-6">
              <SchemeUploadManager
                projectId={id}
                projectType={project.project_type}
                floorCount={project.floor_count}
                developerId={project.developer_id}
              />
            </div>

            <div className="card p-6">
              <UnitTypesManager projectId={id} />
            </div>
          </div>

          <div className="space-y-6">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Project Details
              </h3>

            <div className="space-y-3 text-sm">
              <div>
                <span className="text-secondary-600">Status:</span>
                <span className="ml-2 font-medium text-secondary-900">
                  {project.status.replace('_', ' ').charAt(0).toUpperCase() +
                   project.status.slice(1).replace('_', ' ')}
                </span>
              </div>

              {project.price_range_min && project.price_range_max && (
                <div>
                  <span className="text-secondary-600">Price Range:</span>
                  <span className="ml-2 font-medium text-secondary-900">
                    ${project.price_range_min.toLocaleString()} - $
                    {project.price_range_max.toLocaleString()}
                  </span>
                </div>
              )}

              {project.expected_completion && (
                <div>
                  <span className="text-secondary-600">Completion:</span>
                  <span className="ml-2 font-medium text-secondary-900">
                    {new Date(project.expected_completion).toLocaleDateString('en-NZ', {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              )}

              <div>
                <span className="text-secondary-600">Total Units:</span>
                <span className="ml-2 font-medium text-secondary-900">
                  {units.length}
                </span>
              </div>

              <div>
                <span className="text-secondary-600">Available:</span>
                <span className="ml-2 font-medium text-green-600">
                  {units.filter(u => u.status === 'available').length}
                </span>
              </div>

              <div>
                <span className="text-secondary-600">Reserved:</span>
                <span className="ml-2 font-medium text-yellow-600">
                  {units.filter(u => u.status === 'reserved').length}
                </span>
              </div>

              <div>
                <span className="text-secondary-600">Sold:</span>
                <span className="ml-2 font-medium text-red-600">
                  {units.filter(u => u.status === 'sold').length}
                </span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-secondary-200">
              <button
                onClick={() => navigate(`/projects/${id}`)}
                className="btn-outline w-full py-2 mb-3"
              >
                View Public Page
              </button>
            </div>
          </div>

            <div className="card p-6">
              <MilestoneManager projectId={id} />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'units' && (
        <div className="card p-6">
          <UnitsManager projectId={id} />
        </div>
      )}

      {activeTab === 'discounts' && (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <DiscountConfiguration projectId={id} />
          </div>
          <div>
            <div className="card p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Discount Information
              </h3>
              <p className="text-sm text-secondary-600">
                Configure discount rules and allocations for this project.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'qa' && (
        <div className="space-y-6">
          <div className="card p-6">
            <DeveloperUpdates projectId={id} />
          </div>
          <div className="card p-6">
            <ProjectQA projectId={id} isDeveloper={true} />
          </div>
        </div>
      )}

      {activeTab === 'messages' && (
        <div>
          <MessageCenter developerId={project.developer_id} projectId={id} />
        </div>
      )}
    </div>
  )
}
